import { useState } from "react";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { FaInstagram, FaFacebookF, FaLinkedinIn } from "react-icons/fa";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { FadeUp, Eyebrow, MaskedLines } from "@/components/Reveal";
import { PropertyMap } from "@/components/PropertyMap";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SOCIALS } from "@/lib/brand";

const SOCIAL_ICONS = { instagram: FaInstagram, facebook: FaFacebookF, linkedin: FaLinkedinIn };

const TYPES = ["General", "Rent a Property", "Buy a Property", "Lease Commercial Space", "Property Management"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", inquiry_type: "General", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Message sent — our team will respond promptly.");
      setForm({ name: "", email: "", phone: "", inquiry_type: "General", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-28 md:pt-36 pb-24">
      <Seo
        path="/contact"
        title="Contact Us — Mombasa Real Estate Experts"
        description="Get in touch with SBS Properties. Visit us at SBS Markiti Complex, Mombasa, call +254 709 746 298, or send a message about any property, project or partnership."
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <Eyebrow>Contact</Eyebrow>
        <MaskedLines
          className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
          lines={["Get in", <em key="e" className="italic text-brand">touch.</em>]}
        />
        <FadeUp delay={0.2}>
          <p className="mt-6 max-w-xl text-muted-foreground leading-relaxed">
            Have a question about a property, a project, or a partnership? Send us a message and our team will respond promptly.
          </p>
        </FadeUp>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <FadeUp className="lg:col-span-3">
            <form onSubmit={submit} className="border hairline bg-white p-8 space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Name</label>
                  <Input data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-none" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Email Address</label>
                  <Input data-testid="contact-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 rounded-none" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Phone Number</label>
                  <Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 rounded-none" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Inquiry Type</label>
                  <select
                    data-testid="contact-type-select"
                    value={form.inquiry_type}
                    onChange={(e) => setForm({ ...form, inquiry_type: e.target.value })}
                    className="mt-2 w-full border border-input bg-white px-3 py-2 text-sm outline-none h-9"
                  >
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Message</label>
                <Textarea data-testid="contact-message-input" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 rounded-none" />
              </div>
              <button
                data-testid="contact-submit-button"
                disabled={sending}
                className="bg-brand px-10 py-3.5 text-sm font-semibold text-ivory hover:bg-brand-hover transition-colors disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send Message"}
              </button>
            </form>
          </FadeUp>

          <FadeUp delay={0.15} className="lg:col-span-2 space-y-6">
            <div className="bg-obsidian text-ivory p-8 grain relative" data-testid="contact-info-panel">
              <h3 className="font-serif text-2xl">Contact Information</h3>
              <ul className="mt-6 space-y-5 text-sm text-ivory/75">
                <li className="flex items-start gap-3">
                  <Phone className="h-4 w-4 mt-0.5 text-brand-gold shrink-0" />
                  <a data-testid="contact-phone-link" href="tel:+254709746298" className="hover:text-brand-gold transition-colors">+(254) 709 746 298</a>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="h-4 w-4 mt-0.5 text-brand-gold shrink-0" />
                  <a data-testid="contact-email-link" href="mailto:info@sbsproperties.co.ke" className="hover:text-brand-gold transition-colors">info@sbsproperties.co.ke</a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-0.5 text-brand-gold shrink-0" />
                  <span>SBS Markiti Complex, 1st Floor,<br />PO Box 81497-80100,<br />Mombasa, Kenya</span>
                </li>
              </ul>
              <a
                data-testid="contact-whatsapp-btn"
                href="https://wa.me/254709746298?text=Hello%20SBS%20Properties%2C%20I%27d%20like%20to%20make%20an%20enquiry."
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
                  if (!isMobile) {
                    e.preventDefault();
                    const text = encodeURIComponent("Hello SBS Properties, I'd like to make an enquiry.");
                    window.open(`https://web.whatsapp.com/send?phone=254709746298&text=${text}`, "_blank", "noopener,noreferrer");
                  }
                }}
                className="mt-8 flex items-center justify-center gap-2 border border-brand-gold/60 px-5 py-3 text-sm font-semibold text-brand-gold hover:bg-brand-gold hover:text-obsidian transition-colors"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp Quick Chat
              </a>
              <div className="mt-6 border-t border-white/10 pt-6">
                <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-ivory/50">Follow Us</p>
                <div className="mt-3 flex gap-3">
                  {SOCIALS.map((s) => {
                    const Icon = SOCIAL_ICONS[s.id];
                    return (
                      <a
                        key={s.id}
                        data-testid={`contact-social-${s.id}`}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.label}
                        className="flex h-10 w-10 items-center justify-center border border-white/15 transition-all duration-300 hover:scale-110 hover:bg-white/5"
                        style={{ color: s.color }}
                      >
                        <Icon className="h-5 w-5" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="border hairline overflow-hidden">
              <PropertyMap lat={-4.0580} lng={39.6720} zoom={15} height="300px" />
            </div>
          </FadeUp>
        </div>
      </div>
    </div>
  );
}
